import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  User, Heart, Star, Shield, Activity, ChevronRight, Plus, BarChart,
  FileText, Search, Download, QrCode, X, Lock,
  FolderOpen, Grid3X3, TrendingUp, Award,
  Eye, Users, Share2, Phone, Mail, MapPin, Globe, Settings,
  AlertCircle, Navigation, FileImage, Mic, PenTool, Calendar, Edit
} from 'lucide-react';
import { BarChart as ReBarChart, PieChart, Pie, Cell, Tooltip, Legend, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import QRCode from '../components/QRCode';
import BottomNavigation from '../components/BottomNavigation';
import PullDownMenu from '../components/PullDownMenu';
import SecurityVerification from '../components/SecurityVerification';
import Folder from '../components/Folder';
import LetterGlitch from '../components/LetterGlitch';
import Masonry from '../components/MasonryComponents';
import IOSFilesGrid from '../components/IOSFilesGrid';
import IOSWidgetGrid from '../components/IOSWidgetGrid';
import { useUnifiedStats } from '../hooks/useUnifiedStats';
// Enhanced Utils imports for comprehensive profile data
import { 
  formatPhoneNumber, 
  formatUserProfile, 
  getDefaultUserData, 
  getDefaultEmergencyContacts,
  isValidBloodType
} from '../utils/formatters';
import { 
  sanitizeInput,
  validateProfileData,
  isValidEmail,
  isValidPhoneNumber
} from '../utils/validation';

// Animation variants for modern UI
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100
    }
  }
};

const cardVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { 
    scale: 1, 
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  },
  hover: { 
    scale: 1.02,
    transition: { duration: 0.2 }
  }
};

const DEFAULT_COVER_COLOR = '#2563eb';

const normalizeHexColor = (value, fallback = DEFAULT_COVER_COLOR) => {
  if (typeof value !== 'string') return fallback;
  const clean = value.trim().replace('#', '');

  if (/^[0-9a-fA-F]{3}$/.test(clean)) {
    const expanded = clean.split('').map((char) => char + char).join('');
    return `#${expanded.toLowerCase()}`;
  }

  if (/^[0-9a-fA-F]{6}$/.test(clean)) {
    return `#${clean.toLowerCase()}`;
  }

  return fallback;
};

const hexToRgba = (hex, alpha = 1) => {
  const normalized = normalizeHexColor(hex);
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const safeAlpha = Number.isFinite(alpha) ? Math.min(1, Math.max(0, alpha)) : 1;
  return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
};

// Memoized components for performance
const MemoizedQRCode = React.memo(QRCode);
const MemoizedLetterGlitch = React.memo(LetterGlitch);

const DashboardScreen = () => {
  const { navigateToScreen } = useUI();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Helper functions
  const saveUserPreferences = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString, format = 'short') => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (format === 'short') {
        return date.toLocaleDateString('tr-TR');
      }
      return date.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  };
  
  // Theme state
  const [isDarkModeState, setIsDarkModeState] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState('exhibitions');
  const [showPullDownMenu, setShowPullDownMenu] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  
  // Enhanced states for new features
  const [showQRModal, setShowQRModal] = useState(false);
  const [profileTab, setProfileTab] = useState('identity'); // 'identity' or 'statistics'
  const [searchTerm, setSearchTerm] = useState('');
  const [folderViewMode, setFolderViewMode] = useState('grid'); // 'grid' or 'masonry'
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filesSort, setFilesSort] = useState('recent');
  const [filesDensity, setFilesDensity] = useState('comfortable');
  const [filesPattern, setFilesPattern] = useState(['tall','square','large','wide','square','tall']);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pull-down menu states
  const [pullDownY, setPullDownY] = useState(0);
  const [startY, setStartY] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Profile visibility preferences state
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [profileVisibilityPrefs, setProfileVisibilityPrefs] = useState(() => {
    const saved = localStorage.getItem('profileVisibilityPrefs');
    return saved ? JSON.parse(saved) : {
      showMedicalInfo: true,
      showEmergencyContacts: true, 
      showPersonalInfo: true,
      showPhysicalInfo: true,
      showContactInfo: true,
      showBio: true,
      showHealthData: false,
      allowPublicAccess: false
    };
  });

  // Refs
  const pullDownAreaRef = useRef(null);
  const headerIndicatorRef = useRef(null);
  const pullDownRef = useRef(null);

  // Add a ref to track the current font size scale if needed
  const fontSizeScaleRef = useRef(
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--font-size-scale')) || 1
  );

  // Update the ref when font size changes
  useEffect(() => {
    const handleFontSizeChange = (e) => {
      fontSizeScaleRef.current = e.detail.fontSizeScale;
      // If any specific adjustments are needed for this screen when font size changes
      updateLayoutForFontSize();
    };
    
    const updateLayoutForFontSize = () => {
      // Apply any necessary adjustments based on current font size
      // Example: adjust margins, padding or heights that depend on text size
    };
    
    window.addEventListener('fontSizeChanged', handleFontSizeChange);
    
    return () => {
      window.removeEventListener('fontSizeChanged', handleFontSizeChange);
    };
  }, []);
  
  // Constants for pull-down behavior
  const PULL_THRESHOLD = 60; // Reduced threshold to match MAX_PULL
  const MAX_PULL = 120; // Increased MAX_PULL to provide room for animation
  const PULL_TENSION = 0.5;
  const VELOCITY_THRESHOLD = 0.2; // More sensitive velocity detection
  
  // Security verification handler
  const handleShowSecurityVerification = useCallback((callback) => {
    setShowSecurity(true);
    setTimeout(() => {
      setShowSecurity(false);
      if (callback) callback();
    }, 1500);
  }, []);
  
  // Theme configuration
  const themeConfig = useMemo(() => ({
    dark: {
      bgColor: '#0f172a',
      textColor: 'text-white',
      headerBg: 'bg-slate-800',
      cardBg: 'bg-slate-800/90',
      cardBorder: 'border-slate-700',
      buttonBg: 'bg-blue-600',
      buttonHover: 'hover:bg-blue-700 active:bg-blue-800',
      gradientBg: 'from-slate-900 to-slate-800',
      profileGradient: 'from-indigo-900 to-indigo-800',
      indicatorBg: 'bg-slate-600',
      pullDownBg: 'bg-slate-900/95',
      pullDownBorder: 'border-slate-700',
      badgeColors: {
        red: 'bg-red-900/30 text-red-300 border border-red-800/50',
        blue: 'bg-blue-900/30 text-blue-300 border border-blue-800/50',
        indigo: 'bg-indigo-900/30 text-indigo-300 border border-indigo-800/50',
        teal: 'bg-teal-900/30 text-teal-300 border border-teal-800/50'
      },
      iconColor: 'text-blue-400'
    },
    light: {
      bgColor: '#ffffff',
      textColor: 'text-gray-800',
      headerBg: 'bg-white',
      cardBg: 'bg-white',
      cardBorder: 'border-gray-200',
      buttonBg: 'bg-blue-600',
      buttonHover: 'hover:bg-blue-700 active:bg-blue-800',
      gradientBg: 'from-gray-50 to-white',
      profileGradient: 'from-blue-600 to-indigo-600',
      indicatorBg: 'bg-gray-300',
      pullDownBg: 'bg-white/95',
      pullDownBorder: 'border-gray-200',
      badgeColors: {
        red: 'bg-red-100 text-red-800 border border-red-200',
        blue: 'bg-blue-100 text-blue-800 border border-blue-200',
        indigo: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
        teal: 'bg-teal-100 text-teal-800 border border-teal-200'
      },
      iconColor: 'text-blue-600'
    }
  }), []);
  
  const currentTheme = themeConfig[isDarkModeState ? 'dark' : 'light'];
  
  // Handle touch/mouse start for pull-down menu
  const handleTouchStart = useCallback((e) => {
    // Sadece header alanına dokunulduğunda çalışsın
    if (!pullDownAreaRef.current?.contains(e.target)) return;
    
    const touch = e.touches ? e.touches[0] : e;
    setStartY(touch.clientY);
    setStartTime(Date.now());
    setIsDragging(true);
    setIsTransitioning(false);
  }, []);
  
  // Handle touch/mouse move for pull-down menu
  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    
    const touch = e.touches ? e.touches[0] : e;
    let deltaY = touch.clientY - startY;
    
    // Only handle downward pulls
    if (deltaY > 0) {
      // Apply tension effect with smoother curve
      deltaY = Math.min(deltaY * PULL_TENSION, MAX_PULL);
      
      // Prevent default scrolling behavior when pulling down
      if (e.cancelable) e.preventDefault();
      
      setPullDownY(deltaY);
      
      // Show menu when pulled past threshold
      if (deltaY > PULL_THRESHOLD && !showPullDownMenu) {
        setShowPullDownMenu(true);
      }
    }
  }, [isDragging, startY, showPullDownMenu]);
  
  // Handle touch/mouse end for pull-down menu
  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    const endTime = Date.now();
    const duration = endTime - startTime;
    const velocity = pullDownY / duration;
    
    setIsDragging(false);
    setIsTransitioning(true);
    
    // Determine final state based on position and velocity
    if (pullDownY > PULL_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      setPullDownY(MAX_PULL);
      setShowPullDownMenu(true);
    } else {
      setPullDownY(0);
      setShowPullDownMenu(false);
    }
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, [isDragging, pullDownY, startTime]);
  
  // Enhanced CSS for pull-down menu
  useEffect(() => {
    if (!document.getElementById('enhancedDashboardStyles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'enhancedDashboardStyles';
      styleElement.textContent = `
        /* Pull-down menu styles */
        .pull-down-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          touch-action: none;
          -webkit-user-select: none;
          user-select: none;
        }
        
        .pull-down-menu {
          position: absolute;
          top: -400px;
          left: 0;
          right: 0;
          height: 400px;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-bottom-left-radius: 20px;
          border-bottom-right-radius: 20px;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
          opacity: 0;
          transition: opacity 0.2s ease-out;
        }
        
        .pull-down-menu.visible {
          opacity: 1;
        }
        
        .pull-down-content {
          position: relative;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: 20px;
        }
        
        .pull-indicator {
          width: 36px;
          height: 5px;
          border-radius: 2.5px;
          margin: 0 auto;
          transition: all 0.3s ease;
          position: relative;
        }
        
        .pull-indicator::before,
        .pull-indicator::after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 2.5px;
          background: currentColor;
          opacity: 0;
          transition: all 0.3s ease;
        }
        
        .pull-indicator::before {
          transform: translateY(-8px);
        }
        
        .pull-indicator::after {
          transform: translateY(8px);
        }
        
        .pull-indicator.active::before,
        .pull-indicator.active::after {
          opacity: 0.3;
        }
        
        .pull-down-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 45;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        
        .pull-down-overlay.visible {
          opacity: 1;
          pointer-events: auto;
        }
        
        /* Emergency alert animation */
        @keyframes pulse-emergency {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
        
        .emergency-alert {
          animation: pulse-emergency 2s infinite;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          padding: 2px;
        }
      `;
      document.head.appendChild(styleElement);
    }
  }, []);
  
  // Pull-down area event listeners
  useEffect(() => {
    const area = pullDownAreaRef.current;
    if (!area) return;
    
    // Mouse events
    area.addEventListener('mousedown', handleTouchStart);
    window.addEventListener('mousemove', handleTouchMove);
    window.addEventListener('mouseup', handleTouchEnd);
    
    // Touch events
    area.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      area.removeEventListener('mousedown', handleTouchStart);
      window.removeEventListener('mousemove', handleTouchMove);
      window.removeEventListener('mouseup', handleTouchEnd);
      
      area.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  
  // Animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);
  
  // Theme change listener
  useEffect(() => {
    const handleStorageChange = () => {
      const savedTheme = localStorage.getItem('theme');
      setIsDarkModeState(savedTheme === 'dark');
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // useUnifiedStats hook for unified statistics data
  const { stats: unifiedStats } = useUnifiedStats();

  // Enhanced user data formatting with comprehensive profile support
  // Enhanced user data formatting to match ProfileScreen.jsx
  const formattedUser = useMemo(() => {
    try {
      console.log('🔄 DashboardScreen: Formatting user data:', user);
      const userProfile = formatUserProfile(user);
      const validatedData = validateProfileData(userProfile);
      
      // Store formatted user data for consistency
      saveUserPreferences('formattedUserData', validatedData);
      
      return {
        ...validatedData,
        displayName: sanitizeInput(user?.name || validatedData.name) || 'User',
        safeEmail: isValidEmail(user?.email || validatedData.email) ? (user?.email || validatedData.email) : 'seckin@idapps.com',
        safePhone: isValidPhoneNumber(user?.phone || validatedData.phone) ? formatPhoneNumber(user?.phone || validatedData.phone) : '+90 532 123 45 67',
        safeBio: truncateText(sanitizeInput(user?.bio || validatedData.bio), 200) || 'Teknoloji ve yenilik tutkunu bir profesyonel.',
        notes: sanitizeInput(user?.notes || validatedData.notes) || '',
        hobbies: Array.isArray(user?.hobbies) ? user.hobbies : (validatedData.hobbies || []),
        interests: Array.isArray(user?.interests) ? user.interests : (validatedData.interests || []),
        validatedBloodType: isValidBloodType(user?.bloodType || validatedData.bloodType) ? (user?.bloodType || validatedData.bloodType) : 'AB+',
        
        // Physical characteristics - Enhanced mapping to match ProfileScreen.jsx
        height: user?.height || validatedData.height || '185',
        weight: user?.weight || validatedData.weight || '80',
        bloodType: user?.bloodType || validatedData.bloodType || 'AB+',
        eyeColor: user?.eyeColor || validatedData.eyeColor || 'Kahverengi',
        hairColor: user?.hairColor || validatedData.hairColor || 'Siyah',
        gender: user?.gender || validatedData.gender || '',
        dateOfBirth: user?.dateOfBirth || validatedData.dateOfBirth || '',
        nationality: user?.nationality || validatedData.nationality || '',
        
        // Health data - Enhanced mapping for HealthManager compatibility
        allergies: Array.isArray(user?.allergies) ? user.allergies : (validatedData.allergies || []),
        medicalConditions: Array.isArray(user?.medicalConditions) ? user.medicalConditions : 
                          Array.isArray(user?.conditions) ? user.conditions : (validatedData.medicalConditions || validatedData.conditions || []),
        conditions: Array.isArray(user?.medicalConditions) ? user.medicalConditions : 
                   Array.isArray(user?.conditions) ? user.conditions : (validatedData.conditions || []),
        medications: Array.isArray(user?.medications) ? user.medications : (validatedData.medications || []),
        chronicDiseases: Array.isArray(user?.chronicDiseases) ? user.chronicDiseases : (validatedData.chronicDiseases || []),
        
        emergencyContacts: ((user?.emergencyContacts || validatedData.emergencyContacts) || []).map(contact => ({
          ...contact,
          name: sanitizeInput(contact.name),
          phone: formatPhoneNumber(contact.phone),
          relation: sanitizeInput(contact.relation || contact.relationship),
          relationship: sanitizeInput(contact.relationship || contact.relation)
        })),

        // Personal data - Enhanced mapping
        languages: Array.isArray(user?.languages) ? user.languages : (validatedData.languages || ['Türkçe', 'English', 'Deutsch']),
        
        // Contact information arrays
        phones: Array.isArray(user?.phones) ? user.phones : [user?.phone || validatedData.phone || '+90 532 123 45 67'],
        emails: Array.isArray(user?.emails) ? user.emails : [user?.email || validatedData.email || 'seckin@idapps.com'],
        address: user?.address || validatedData.address || 'İstanbul, Türkiye',
        
        // Profile visibility preferences
        profileVisibility: user?.profileVisibility || {
          showMedicalInfo: true,
          showEmergencyContacts: true, 
          showPersonalInfo: true,
          showContactInfo: true,
          showBio: true,
          showHealthData: false,
          allowPublicAccess: false
        }
      };
    } catch (error) {
      console.error('Error formatting user data:', error);
      return getDefaultUserData();
    }
  }, [user]);

  const dashboardCoverColor = useMemo(
    () => normalizeHexColor(user?.profileCoverColor || formattedUser?.profileCoverColor || DEFAULT_COVER_COLOR),
    [user?.profileCoverColor, formattedUser?.profileCoverColor]
  );

  const dashboardCoverUri = useMemo(() => {
    const rawCover = user?.profileCover || formattedUser?.profileCover || '';
    return typeof rawCover === 'string' && rawCover.trim() ? rawCover.trim() : '';
  }, [user?.profileCover, formattedUser?.profileCover]);

  const dashboardCoverOverlayEnabled = useMemo(() => {
    if (typeof user?.profileCoverOverlayEnabled === 'boolean') return user.profileCoverOverlayEnabled;
    if (typeof formattedUser?.profileCoverOverlayEnabled === 'boolean') return formattedUser.profileCoverOverlayEnabled;
    return true;
  }, [user?.profileCoverOverlayEnabled, formattedUser?.profileCoverOverlayEnabled]);

  const dashboardCoverOverlayOpacity = useMemo(() => {
    const rawOpacity = user?.profileCoverOverlayOpacity ?? formattedUser?.profileCoverOverlayOpacity;
    if (typeof rawOpacity !== 'number' || Number.isNaN(rawOpacity)) return 0.45;
    return Math.min(0.95, Math.max(0, rawOpacity));
  }, [user?.profileCoverOverlayOpacity, formattedUser?.profileCoverOverlayOpacity]);

  const dashboardMainSurfaceStyle = useMemo(() => ({
    backgroundColor: isDarkModeState ? '#020617' : '#f8fafc',
    backgroundImage: isDarkModeState
      ? `linear-gradient(180deg, ${hexToRgba(dashboardCoverColor, 0.22)} 0%, rgba(2,6,23,0.86) 52%, rgba(2,6,23,0.95) 100%)`
      : `linear-gradient(180deg, ${hexToRgba(dashboardCoverColor, 0.16)} 0%, rgba(239,246,255,0.82) 48%, rgba(248,250,252,0.92) 100%)`
  }), [isDarkModeState, dashboardCoverColor]);

  const dashboardTextureStyle = useMemo(() => ({
    backgroundImage: `radial-gradient(circle at 18% 20%, ${hexToRgba(dashboardCoverColor, isDarkModeState ? 0.28 : 0.2)} 0%, transparent 38%),
      radial-gradient(circle at 82% 8%, ${hexToRgba(dashboardCoverColor, isDarkModeState ? 0.2 : 0.14)} 0%, transparent 32%),
      radial-gradient(circle at 76% 78%, ${hexToRgba(dashboardCoverColor, isDarkModeState ? 0.14 : 0.1)} 0%, transparent 30%)`
  }), [dashboardCoverColor, isDarkModeState]);

  const profileHeaderSurfaceStyle = useMemo(() => {
    const safeCoverUri = dashboardCoverUri.replace(/"/g, '\\"');
    const overlayStrong = dashboardCoverOverlayEnabled
      ? Math.min(0.95, Math.max(0.2, dashboardCoverOverlayOpacity + (isDarkModeState ? 0.22 : 0.12)))
      : (isDarkModeState ? 0.75 : 0.5);
    const overlaySoft = dashboardCoverOverlayEnabled
      ? Math.max(0.16, dashboardCoverOverlayOpacity * 0.55)
      : (isDarkModeState ? 0.45 : 0.28);

    if (safeCoverUri) {
      return {
        backgroundImage: `linear-gradient(135deg, ${hexToRgba(dashboardCoverColor, overlayStrong)} 0%, ${hexToRgba(dashboardCoverColor, overlaySoft)} 52%, ${isDarkModeState ? 'rgba(2,6,23,0.82)' : 'rgba(15,23,42,0.45)'} 100%), url("${safeCoverUri}")`,
        backgroundPosition: 'center',
        backgroundSize: 'cover'
      };
    }

    return {
      backgroundImage: `linear-gradient(135deg, ${hexToRgba(dashboardCoverColor, isDarkModeState ? 0.92 : 0.86)} 0%, ${hexToRgba(dashboardCoverColor, isDarkModeState ? 0.66 : 0.58)} 100%)`
    };
  }, [
    dashboardCoverUri,
    dashboardCoverColor,
    dashboardCoverOverlayEnabled,
    dashboardCoverOverlayOpacity,
    isDarkModeState
  ]);

  // Media-centric categories for public profile (Exhibitions tab)
  const mediaCategories = useMemo(() => [
    { 
      id: 1, 
      title: t('exhibitions.videoAndPhotos', 'Video ve Fotoğraflar'), 
      color: "#e11d48", 
      items: 24,
      lastUpdated: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      icon: FileImage,
      description: t('exhibitions.videoAndPhotosDesc', 'Fotoğraflar, videolar ve görsel içerikler'),
      contentTypes: ['photos', 'videos', 'images', 'galleries'],
      isPublic: true
    },
    { 
      id: 2, 
      title: t('exhibitions.files', 'Dosyalar'), 
      color: "#0ea5e9", 
      items: 18,
      lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      icon: FileText,
      description: t('exhibitions.filesDesc', 'Belgeler, PDF\'ler ve diğer dosyalar'),
      contentTypes: ['documents', 'pdfs', 'presentations', 'spreadsheets'],
      isPublic: true
    },
    { 
      id: 3, 
      title: t('exhibitions.audio', 'Sesler'), 
      color: "#8b5cf6", 
      items: 12,
      lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      icon: Mic,
      description: t('exhibitions.audioDesc', 'Ses kayıtları, müzik ve sesli notlar'),
      contentTypes: ['voice_memos', 'music', 'audio_recordings', 'podcasts'],
      isPublic: true
    },
    { 
      id: 4, 
      title: t('exhibitions.writings', 'Yazılar'), 
      color: "#059669", 
      items: 9,
      lastUpdated: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      icon: PenTool,
      description: t('exhibitions.writingsDesc', 'Makaleler, notlar ve yazılı içerikler'),
      contentTypes: ['articles', 'notes', 'blog_posts', 'journals'],
      isPublic: true
    }
  ], [t]);

  // Sample content data for each media category
  const getCategoryContent = useCallback((categoryId) => {
    const sampleContent = {
      1: [ // Video ve Fotoğraflar
        { id: 1, name: 'Tatil Fotoğrafları', type: 'photos', size: '24 MB', date: '2024-01-15', thumbnail: '/api/placeholder/300/400', img: '/api/placeholder/300/400', height: 400, url: '#', isPublic: true },
        { id: 2, name: 'Doğum Günü Videosu', type: 'video', size: '156 MB', date: '2024-01-10', thumbnail: '/api/placeholder/300/250', img: '/api/placeholder/300/250', height: 250, url: '#', isPublic: true },
        { id: 3, name: 'Aile Portresi', type: 'photo', size: '8 MB', date: '2024-01-05', thumbnail: '/api/placeholder/300/350', img: '/api/placeholder/300/350', height: 350, url: '#', isPublic: true },
        { id: 4, name: 'Mezuniyet Videosu', type: 'video', size: '89 MB', date: '2023-12-20', thumbnail: '/api/placeholder/300/300', img: '/api/placeholder/300/300', height: 300, url: '#', isPublic: true },
        { id: 5, name: 'Doğa Fotoğrafları', type: 'photos', size: '15 MB', date: '2024-01-12', thumbnail: '/api/placeholder/300/450', img: '/api/placeholder/300/450', height: 450, url: '#', isPublic: true },
        { id: 6, name: 'Şehir Manzarası', type: 'photo', size: '12 MB', date: '2024-01-08', thumbnail: '/api/placeholder/300/200', img: '/api/placeholder/300/200', height: 200, url: '#', isPublic: true }
      ],
      2: [ // Dosyalar
        { id: 7, name: 'CV_2024.pdf', type: 'document', size: '2.5 MB', date: '2024-01-20', thumbnail: '/api/placeholder/300/400', img: '/api/placeholder/300/400', height: 400, url: '#', isPublic: true },
        { id: 8, name: 'Sertifikalarım.pdf', type: 'document', size: '12 MB', date: '2024-01-18', thumbnail: '/api/placeholder/300/300', img: '/api/placeholder/300/300', height: 300, url: '#', isPublic: true },
        { id: 9, name: 'Proje Sunumu.pptx', type: 'presentation', size: '45 MB', date: '2024-01-15', thumbnail: '/api/placeholder/300/350', img: '/api/placeholder/300/350', height: 350, url: '#', isPublic: true },
        { id: 10, name: 'İş Deneyimi.docx', type: 'document', size: '1.8 MB', date: '2024-01-12', thumbnail: '/api/placeholder/300/250', img: '/api/placeholder/300/250', height: 250, url: '#', isPublic: true },
        { id: 11, name: 'Araştırma Raporu.pdf', type: 'document', size: '8.3 MB', date: '2024-01-10', thumbnail: '/api/placeholder/300/380', img: '/api/placeholder/300/380', height: 380, url: '#', isPublic: true }
      ],
      3: [ // Sesler
        { id: 12, name: 'Gitar Performansı', type: 'audio', size: '15 MB', date: '2024-01-16', thumbnail: '/api/placeholder/300/200', img: '/api/placeholder/300/200', height: 200, url: '#', isPublic: true },
        { id: 13, name: 'Podcast Kayıtı', type: 'audio', size: '67 MB', date: '2024-01-14', thumbnail: '/api/placeholder/300/300', img: '/api/placeholder/300/300', height: 300, url: '#', isPublic: true },
        { id: 14, name: 'Şarkı Söyleme', type: 'audio', size: '22 MB', date: '2024-01-08', thumbnail: '/api/placeholder/300/250', img: '/api/placeholder/300/250', height: 250, url: '#', isPublic: true },
        { id: 15, name: 'Konuşma Kayıtları', type: 'audio', size: '33 MB', date: '2024-01-06', thumbnail: '/api/placeholder/300/180', img: '/api/placeholder/300/180', height: 180, url: '#', isPublic: true }
      ],
      4: [ // Yazılar
        { id: 16, name: 'Teknoloji Üzerine Düşünceler', type: 'article', size: '0.5 MB', date: '2024-01-19', thumbnail: '/api/placeholder/300/350', img: '/api/placeholder/300/350', height: 350, url: '#', isPublic: true },
        { id: 17, name: 'Kişisel Gelişim Notları', type: 'notes', size: '1.2 MB', date: '2024-01-17', thumbnail: '/api/placeholder/300/280', img: '/api/placeholder/300/280', height: 280, url: '#', isPublic: true },
        { id: 18, name: 'Seyahat Günlüğü', type: 'journal', size: '2.8 MB', date: '2024-01-13', thumbnail: '/api/placeholder/300/320', img: '/api/placeholder/300/320', height: 320, url: '#', isPublic: true },
        { id: 19, name: 'Blog Yazısı Taslağı', type: 'article', size: '0.8 MB', date: '2024-01-11', thumbnail: '/api/placeholder/300/400', img: '/api/placeholder/300/400', height: 400, url: '#', isPublic: true },
        { id: 20, name: 'Felsefe Notları', type: 'notes', size: '1.5 MB', date: '2024-01-09', thumbnail: '/api/placeholder/300/200', img: '/api/placeholder/300/200', height: 200, url: '#', isPublic: true }
      ]
    };
    return sampleContent[categoryId] || [];
  }, []);

  // Handle back navigation from category view
  const handleBackToCategories = useCallback(() => {
    setSelectedCategory(null);
  }, []);

  // Enhanced QR data with user's actual QR
  const qrData = useMemo(() => ({
    id: user?.id || '#ID12345',
    name: user?.name || formattedUser?.displayName || t('profile.defaultName', 'Kullanıcı'),
    url: `https://idapps.io/id/${user?.id || 'demo'}`,
    type: 'identity',
    createdAt: user?.createdAt || new Date().toISOString(),
    viewCount: user?.qrViewCount || 0,
    isActive: true
  }), [user, formattedUser, t]);

  // Profile tabs for Identity section
  const profileTabs = useMemo(() => [
    { id: 'identity', label: t('profile.identity', 'Kimlik'), icon: User },
    { id: 'statistics', label: t('profile.statistics', 'İstatistikler'), icon: BarChart }
  ], [t]);

  // Achievements data
  const achievements = useMemo(() => [
    { id: 1, title: t('achievements.earlyAdopter', 'Erken Kullanıcı'), icon: Star, color: 'gold', earned: true },
    { id: 2, title: t('achievements.dataProtector', 'Veri Koruyucu'), icon: Shield, color: 'blue', earned: true },
    { id: 3, title: t('achievements.socialConnector', 'Sosyal Bağlayıcı'), icon: Users, color: 'green', earned: false },
    { id: 4, title: t('achievements.healthConscious', 'Sağlık Bilinci'), icon: Heart, color: 'red', earned: true }
  ], [t]);

  // Statistics data structure
  const statisticsData = useMemo(() => ({
    qrStats: {
      totalScans: unifiedStats?.qrStats?.totalViews || 156,
      weeklyGrowth: unifiedStats?.qrStats?.weeklyGrowth || 23,
      topLocations: ['İstanbul', 'Ankara', 'İzmir'],
      peakHours: ['14:00-16:00', '19:00-21:00']
    },
    profileViews: {
      total: 1250,
      thisWeek: 89,
      countries: 12,
      avgTime: '2m 34s'
    },
    engagement: {
      shares: 45,
      connections: 128,
      messages: 23,
      rating: 4.8
    }
  }), [unifiedStats, t]);

  // Basic folders data for compatibility
  const folders = useMemo(() => [
    { id: 1, title: t('dashboard.folders.personal', 'Kişisel'), color: "#4f46e5", items: 12 },
    { id: 2, title: t('dashboard.folders.health', 'Sağlık'), color: "#06b6d4", items: 8 },
    { id: 3, title: t('dashboard.folders.documents', 'Belgeler'), color: "#10b981", items: 15 },
    { id: 4, title: t('dashboard.folders.favorite', 'Favoriler'), color: "#ef4444", items: 5 }
  ], [t]);

  // QR Code data for compatibility
  const qrCode = useMemo(() => ({
    id: user?.id || '#ID12345',
    name: user?.name || formattedUser?.displayName || t('profile.defaultName', 'Kullanıcı')
  }), [user, formattedUser, t]);

  // Helper functions
  const openInMaps = (coordinates) => {
    if (coordinates) {
      window.open(`https://maps.google.com/?q=${coordinates.lat},${coordinates.lng}`, '_blank');
    }
  };

  // Event handlers
  const handleFolderClick = useCallback((folder) => {
    navigateToScreen('folder', { folderId: folder.id });
  }, [navigateToScreen]);

  const handleCategoryClick = useCallback((category) => {
    // Set selected category to show its contents
    setSelectedCategory(category);
  }, []);

  const handleQRClick = useCallback(() => {
    setShowQRModal(true);
  }, []);

  const handleDownloadQR = useCallback(() => {
    // QR download logic here
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    
    // QR generation and download
    const link = document.createElement('a');
    link.download = `qr-code-${qrData.id}.png`;
    link.href = canvas.toDataURL();
    link.click();
  }, [qrData.id]);

  const handleShareQR = useCallback(() => {
    if (navigator.share) {
      navigator.share({
        title: t('qr.shareTitle', 'QR Kimliğimi Paylaş'),
        text: t('qr.shareText', 'Benim QR kimliğim'),
        url: qrData.url
      });
    }
  }, [qrData.url, t]);

  // Profile visibility preferences handler
  const handleProfileVisibilityChange = useCallback((key, value) => {
    setProfileVisibilityPrefs(prev => {
      const updated = { ...prev, [key]: value };
      localStorage.setItem('profileVisibilityPrefs', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Add emergency contact handler
  const handleAddEmergencyContact = useCallback(() => {
    console.log('🔄 DashboardScreen: Navigating to profile edit for adding emergency contact...');
    navigateToScreen('profileEdit', { section: 'emergency' });
  }, [navigateToScreen]);

  // Data
  const tabItems = [
    { id: 'exhibitions', label: t('dashboard.exhibitions', 'Sergilenenler') },
    { id: 'identity', label: t('dashboard.identity', 'Kimlik') },
    { id: 'statistics', label: t('dashboard.statistics', 'İstatistikler') }
  ];
  
  
  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className={`flex flex-col h-screen relative overflow-hidden ${currentTheme.textColor}`}
      style={{ backgroundColor: currentTheme.bgColor }}
    >
      <div className="absolute inset-0 z-0 pointer-events-none">
        {dashboardCoverUri ? (
          <img
            src={dashboardCoverUri}
            alt="Dashboard Cover"
            className="w-full h-full object-cover"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
            style={{
              opacity: isDarkModeState ? 0.2 : 0.16,
              filter: isDarkModeState ? 'saturate(1.15) contrast(1.05)' : 'saturate(1.05) contrast(1.02)'
            }}
          />
        ) : null}
        <div className="absolute inset-0" style={dashboardTextureStyle}></div>
      </div>

      {/* Background */}
      {isDarkModeState && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <MemoizedLetterGlitch
            glitchColors={['#1e40af', '#3b82f6', '#60a5fa']}
            glitchSpeed={50}
            centerVignette={true}
            outerVignette={false}
            smooth={true}
            density={0.25}
            fadeInSpeed={1.5}
            textGlowColor="rgba(96, 165, 250, 0.8)"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(2,6,23,0.54)' }}></div>
        </div>
      )}
      
      {/* Pull-down area */}
      <div 
        ref={pullDownAreaRef} 
        className="absolute top-0 left-0 right-0 h-24 z-40"
      ></div>
      
      {/* Pull-down Menu and Overlay */}
      <div className="pull-down-container" style={{ zIndex: 999 }}>
        <PullDownMenu 
          onItemSelect={(screen) => {
            navigateToScreen(screen);
            setPullDownY(0);
            setShowPullDownMenu(false);
          }}
          onShowSecurityVerification={handleShowSecurityVerification}
          isDarkMode={isDarkModeState}
          showPullDownMenu={showPullDownMenu}
          pullDownProgress={pullDownY > 0 ? (pullDownY / MAX_PULL) * 100 : 0}
          closePullDownMenu={() => {
            setPullDownY(0);
            setShowPullDownMenu(false);
          }}
        />
      </div>
      
      {/* Overlay */}
      <div 
        className={`pull-down-overlay ${showPullDownMenu ? 'visible' : ''}`}
        onClick={() => {
          setPullDownY(0);
          setShowPullDownMenu(false);
        }}
      />
      
      {/* Enhanced Main Content */}
      <motion.main 
        className="flex-1 overflow-auto pb-20 relative z-10"
        style={{
          transform: `translateY(${Math.min(pullDownY, MAX_PULL)}px)`,
          transition: isTransitioning ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          ...dashboardMainSurfaceStyle
        }}
      >
        {/* Enhanced Profile Header */}
        <motion.div 
          variants={itemVariants}
          className="text-white p-6 relative overflow-hidden"
          style={profileHeaderSurfaceStyle}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div className="relative z-10 flex flex-col items-center space-y-3">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className={`w-28 h-28 rounded-full flex items-center justify-center shadow-2xl ${
                isDarkModeState ? 'bg-slate-700' : 'bg-white'
              } overflow-hidden border-4 border-white/20`}
            >
              {user?.profilePhoto ? (
                <img 
                  src={user.profilePhoto} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <User size={72} className={currentTheme.iconColor} style={{display: user?.profilePhoto ? 'none' : 'block'}} />
            </motion.div>
            
            <div className="text-center">
              <h2 className="text-2xl font-bold">{formattedUser?.displayName}</h2>
              <p className="text-lg opacity-90">@{user?.username || 'user'}</p>
              {user?.bio && (
                <p className="text-sm text-white/80 text-center max-w-xs mt-2">{sanitizeInput(user.bio)}</p>
              )}
            </div>
          </div>
          
          {/* Enhanced Profile Stats */}
          <motion.div 
            variants={containerVariants}
            className="flex justify-around mt-6 pt-4 border-t border-white/20"
          >
            <motion.div variants={itemVariants} className="text-center">
              <div className="flex flex-col items-center space-y-1">
                <div className="p-2 rounded-full bg-white/20">
                  <Shield size={20} />
                </div>
                <span className="text-xs font-medium">{t('security.security', 'Güvenlik')}</span>
                <span className="font-bold text-lg">A+</span>
              </div>
            </motion.div>
            <motion.div variants={itemVariants} className="text-center">
              <div className="flex flex-col items-center space-y-1">
                <div className="p-2 rounded-full bg-white/20">
                  <Star size={20} />
                </div>
                <span className="text-xs font-medium">{t('dashboard.badges', 'Rozetler')}</span>
                <span className="font-bold text-lg">{achievements.filter(a => a.earned).length}</span>
              </div>
            </motion.div>
            <motion.div variants={itemVariants} className="text-center">
              <div className="flex flex-col items-center space-y-1">
                <div className="p-2 rounded-full bg-white/20">
                  <Activity size={20} />
                </div>
                <span className="text-xs font-medium">{t('dashboard.activity', 'Aktivite')}</span>
                <span className="font-bold text-lg">{t('dashboard.active', 'Aktif')}</span>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
        
        {/* Enhanced Tab Navigation */}
        <motion.div 
          variants={itemVariants}
          className={`${currentTheme.headerBg} border-b ${isDarkModeState ? 'border-slate-700' : 'border-gray-200'} sticky top-0 z-20 backdrop-blur-sm`}
        >
          <div className="flex">
            {tabItems.map(tab => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 px-4 text-center font-semibold transition-all duration-300 relative ${
                  activeTab === tab.id
                    ? `${isDarkModeState ? 'text-blue-400' : 'text-blue-600'}`
                    : `${isDarkModeState ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTab"
                    className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDarkModeState ? 'bg-blue-400' : 'bg-blue-600'}`}
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </motion.div>
        
        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'exhibitions' && (
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {selectedFolder === null && selectedCategory === null ? (
                  <>
                    {/* iOS 16 Style Media Categories Section */}
                    <motion.div 
                      variants={itemVariants} 
                      className="mb-6"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ 
                        duration: 0.6,
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                    >
                      {/* Minimal Header */}
                      <div className="flex items-center justify-between mb-4">
                        <motion.h3 
                          className={`text-lg font-semibold ${currentTheme.textColor}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          {t('exhibitions.myCollections', 'Koleksiyonlarım')}
                        </motion.h3>
                        
                        {/* Search with animation */}
                        <motion.div 
                          className="relative"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          <Search size={14} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkModeState ? 'text-gray-400' : 'text-gray-500'}`} />
                          <input
                            type="text"
                            placeholder={t('exhibitions.searchPlaceholder', 'Ara...')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`pl-9 pr-3 py-2 w-32 rounded-full text-sm transition-all duration-300 focus:w-48 ${
                              isDarkModeState 
                                ? 'bg-slate-800/50 border-slate-700 text-white placeholder-gray-400' 
                                : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                            } border focus:outline-none focus:border-blue-400 backdrop-blur-sm`}
                          />
                        </motion.div>
                      </div>
                      
                      {/* iOS 16 Style Cards Grid */}
                      <motion.div 
                        className="grid grid-cols-2 gap-3"
                        variants={{
                          hidden: { opacity: 0 },
                          visible: {
                            opacity: 1,
                            transition: {
                              staggerChildren: 0.1
                            }
                          }
                        }}
                        initial="hidden"
                        animate="visible"
                      >
                        {mediaCategories
                          .filter(category => category.title.toLowerCase().includes(searchTerm.toLowerCase()))
                          .map((category, index) => (
                          <motion.div
                            key={category.id}
                            variants={{
                              hidden: { 
                                opacity: 0, 
                                y: 20,
                                scale: 0.9
                              },
                              visible: { 
                                opacity: 1, 
                                y: 0,
                                scale: 1,
                                transition: {
                                  type: "spring",
                                  damping: 20,
                                  stiffness: 300
                                }
                              }
                            }}
                            whileHover={{ 
                              scale: 1.02,
                              y: -2,
                              transition: { duration: 0.2 }
                            }}
                            whileTap={{ 
                              scale: 0.98,
                              transition: { duration: 0.1 }
                            }}
                            className={`relative overflow-hidden rounded-2xl cursor-pointer group ${
                              isDarkModeState ? 'bg-slate-800/30' : 'bg-white/70'
                            } backdrop-blur-xl border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300`}
                            onClick={() => handleCategoryClick(category)}
                            style={{
                              background: isDarkModeState 
                                ? `linear-gradient(135deg, ${category.color}08, ${category.color}03)`
                                : `linear-gradient(135deg, ${category.color}10, ${category.color}05)`
                            }}
                          >
                            {/* Animated Background Gradient */}
                            <motion.div 
                              className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500"
                              style={{ 
                                background: `radial-gradient(circle at 50% 50%, ${category.color}40, transparent 70%)`
                              }}
                            />
                            
                            {/* Content */}
                            <div className="relative z-10 p-4 h-28 flex flex-col justify-between">
                              <div className="flex items-start justify-between">
                                {/* Icon with animation */}
                                <motion.div 
                                  className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                                  style={{ 
                                    background: `linear-gradient(135deg, ${category.color}20, ${category.color}40)`,
                                    boxShadow: `0 4px 20px ${category.color}20`
                                  }}
                                  whileHover={{ 
                                    scale: 1.1,
                                    rotate: 5,
                                    transition: { duration: 0.2 }
                                  }}
                                >
                                  <category.icon 
                                    size={20} 
                                    style={{ color: category.color }}
                                  />
                                </motion.div>
                                
                                {/* Item count badge */}
                                <motion.div 
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    isDarkModeState ? 'bg-slate-700/50 text-gray-300' : 'bg-white/60 text-gray-600'
                                  } backdrop-blur-sm`}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: 0.2 + index * 0.1 }}
                                >
                                  {category.items}
                                </motion.div>
                              </div>
                              
                              {/* Title */}
                              <div>
                                <motion.h4 
                                  className={`font-semibold text-sm ${currentTheme.textColor} mb-1`}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 0.3 + index * 0.1 }}
                                >
                                  {category.title}
                                </motion.h4>
                                
                                {/* Minimal subtitle */}
                                <motion.p 
                                  className={`text-xs ${isDarkModeState ? 'text-gray-400' : 'text-gray-500'} opacity-80`}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 0.4 + index * 0.1 }}
                                >
                                  {new Date(category.lastUpdated).toLocaleDateString('tr-TR', { 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}
                                </motion.p>
                              </div>
                            </div>
                            
                            {/* Subtle shine effect */}
                            <motion.div
                              className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
                              style={{
                                background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                              }}
                              initial={{ x: '-100%' }}
                              whileHover={{ 
                                x: '100%',
                                transition: { duration: 0.6, ease: "easeInOut" }
                              }}
                            />
                          </motion.div>
                        ))}
                      </motion.div>
                      
                      {/* Elegant Empty State */}
                      {searchTerm && mediaCategories.filter(category => category.title.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.4 }}
                          className="text-center py-12"
                        >
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring", damping: 15 }}
                            className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                              isDarkModeState ? 'bg-slate-800/50' : 'bg-gray-100/80'
                            } backdrop-blur-sm`}
                          >
                            <Search size={24} className={`${isDarkModeState ? 'text-gray-500' : 'text-gray-400'}`} />
                          </motion.div>
                          <motion.h3 
                            className={`text-lg font-medium mb-2 ${currentTheme.textColor}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                          >
                            {t('exhibitions.noResults', 'Sonuç bulunamadı')}
                          </motion.h3>
                          <motion.p 
                            className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-500'}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                          >
                            {t('exhibitions.tryDifferentTerm', 'Farklı bir terim deneyin')}
                          </motion.p>
                        </motion.div>
                      )}
                    </motion.div>
                  </>
                ) : selectedCategory && (
                  /* iOS 16 Style Category Content View */
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ 
                      duration: 0.5,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                    className="mb-6"
                  >
                    {/* Elegant Category Header */}
                    <motion.div 
                      className="flex items-center mb-6"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <motion.button
                        onClick={handleBackToCategories}
                        className={`p-2.5 rounded-full mr-4 ${
                          isDarkModeState ? 'bg-slate-800/50' : 'bg-gray-100/80'
                        } backdrop-blur-sm border border-white/20 shadow-sm`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <ChevronRight size={18} className={`${currentTheme.iconColor} transform rotate-180`} />
                      </motion.button>
                      
                      <div className="flex items-center space-x-3 flex-1">
                        <motion.div 
                          className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md"
                          style={{ 
                            background: `linear-gradient(135deg, ${selectedCategory.color}20, ${selectedCategory.color}40)`,
                            boxShadow: `0 4px 20px ${selectedCategory.color}15`
                          }}
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.2 }}
                        >
                          <selectedCategory.icon size={26} style={{ color: selectedCategory.color }} />
                        </motion.div>
                        <div className="flex-1">
                          <motion.h3 
                            className={`text-xl font-semibold ${currentTheme.textColor}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                          >
                            {selectedCategory.title}
                          </motion.h3>
                          <motion.div 
                            className="flex items-center space-x-3 mt-1"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 }}
                          >
                            <span className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-500'}`}>
                              {t('exhibitions.itemsCount', '{count} öğe', { count: getCategoryContent(selectedCategory.id).length })}
                            </span>
                          </motion.div>
                        </div>
                      </div>
                    </motion.div>

                    {/* iOS Widget tarzı ızgara: tüm kategorilerde */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="space-y-4"
                    >
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>Sırala</span>
                          <select
                            value={filesSort}
                            onChange={(e) => setFilesSort(e.target.value)}
                            className={`text-sm rounded-xl px-3 py-1.5 border outline-none ${isDarkModeState ? 'bg-slate-900 border-slate-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
                          >
                            <option value="recent">En Yeni</option>
                            <option value="name">Ada Göre</option>
                            <option value="size">Boyuta Göre</option>
                            <option value="type">Türe Göre</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>Yoğunluk</span>
                          <select
                            value={filesDensity}
                            onChange={(e) => setFilesDensity(e.target.value)}
                            className={`text-sm rounded-xl px-3 py-1.5 border outline-none ${isDarkModeState ? 'bg-slate-900 border-slate-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
                          >
                            <option value="comfortable">Rahat</option>
                            <option value="compact">Sıkışık</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>Düzen</span>
                          <select
                            value="karışık"
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === 'sütun') setFilesPattern(['square','tall','square','tall']);
                              else if (v === 'karışık') setFilesPattern(['tall','square','large','wide','square','tall']);
                              else setFilesPattern(['tall','square','wide','square']);
                            }}
                            className={`text-sm rounded-xl px-3 py-1.5 border outline-none ${isDarkModeState ? 'bg-slate-900 border-slate-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
                          >
                            <option value="karışık">Karışık</option>
                            <option value="sütun">Sütun</option>
                            <option value="özel">Özel</option>
                          </select>
                        </div>
                      </div>

                      <IOSWidgetGrid
                        items={getCategoryContent(selectedCategory.id)}
                        sort={filesSort}
                        density={filesDensity}
                        columns={2}
                        pattern={selectedCategory.id === 1 ? ['wide','square','tall','large','square','square'] : filesPattern}
                        onItemClick={(item) => console.log('open item', item)}
                      />
                    </motion.div>

                    {/* Elegant Empty State */}
                    {getCategoryContent(selectedCategory.id).length === 0 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className="text-center py-16"
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.2, type: "spring", damping: 15 }}
                          className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
                            isDarkModeState ? 'bg-slate-800/50' : 'bg-gray-100/80'
                          } backdrop-blur-sm shadow-lg`}
                          style={{ 
                            background: `linear-gradient(135deg, ${selectedCategory.color}10, ${selectedCategory.color}05)`
                          }}
                        >
                          <selectedCategory.icon size={32} style={{ color: selectedCategory.color }} />
                        </motion.div>
                        <motion.h3 
                          className={`text-lg font-medium mb-2 ${currentTheme.textColor}`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          {t('exhibitions.noContentYet', 'Henüz içerik yok')}
                        </motion.h3>
                        <motion.p 
                          className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-500'} mb-6`}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          {t('exhibitions.addFirstContent', 'Bu kategoriye ilk içeriği ekleyin')}
                        </motion.p>
                        {user?.id && (
                          <motion.button
                            className={`px-6 py-3 rounded-full font-medium shadow-lg transition-all duration-300 ${
                              isDarkModeState 
                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                                : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                            }`}
                            style={{
                              background: `linear-gradient(135deg, ${selectedCategory.color}, ${selectedCategory.color}cc)`
                            }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {t('exhibitions.addContent', 'İçerik Ekle')}
                          </motion.button>
                        )}
                      </motion.div>
                    )}
                  </motion.div>
                )}

                {/* Enhanced QR Identity Section */}
                {selectedCategory === null && (
                  <motion.div variants={itemVariants} className="mb-8">
                      <h3 className={`font-bold text-xl mb-6 flex items-center ${currentTheme.textColor}`}>
                        <QrCode size={24} className="text-green-500 mr-3" />
                        {t('dashboard.qrIdentity', 'QR Kimlik')}
                      </h3>
                      
                      <motion.div
                        variants={cardVariants}
                        whileHover="hover"
                        className={`${currentTheme.cardBg} rounded-xl p-6 ${currentTheme.cardBorder} border shadow-sm relative overflow-hidden group cursor-pointer`}
                        onClick={handleQRClick}
                      >
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-5">
                          <div className="w-full h-full" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000' fill-opacity='0.1'%3E%3Crect width='2' height='2'/%3E%3C/g%3E%3C/svg%3E")`,
                            backgroundSize: '20px 20px'
                          }} />
                        </div>
                        
                        <div className="relative z-10 flex flex-col items-center">
                          <div className="bg-white p-3 rounded-lg shadow-md mb-4 group-hover:scale-105 transition-transform">
                            <MemoizedQRCode 
                              data={qrData.url}
                              name={qrData.name}
                              id={qrData.id}
                              size="medium"
                              isDarkMode={false}
                            />
                          </div>
                          
                          <div className="text-center">
                            <h4 className={`text-lg font-semibold mb-2 ${currentTheme.textColor}`}>
                              {qrData.name}
                            </h4>
                            <p className={`text-sm mb-4 ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>
                              ID: {qrData.id}
                            </p>
                            
                            <div className="flex items-center justify-center space-x-4 text-xs">
                              <div className="flex items-center space-x-1">
                                <Eye size={14} className="text-blue-500" />
                                <span className={isDarkModeState ? 'text-gray-400' : 'text-gray-600'}>
                                  {qrData.viewCount} {t('dashboard.views', 'görüntülenme')}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Share2 size={14} className="text-green-500" />
                                <span className={isDarkModeState ? 'text-gray-400' : 'text-gray-600'}>
                                  {t('dashboard.tapToShare', 'Paylaşmak için dokun')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}

                    {/* QR Modal */}
                    <AnimatePresence>
                      {showQRModal && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                          onClick={() => setShowQRModal(false)}
                        >
                          <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className={`${currentTheme.cardBg} rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="text-center">
                              <h3 className={`text-xl font-bold mb-4 ${currentTheme.textColor}`}>
                                {t('qr.myQRCode', 'QR Kodım')}
                              </h3>
                              
                              <div className="bg-white p-4 rounded-xl mb-6 inline-block">
                                <MemoizedQRCode 
                                  data={qrData.url}
                                  name={qrData.name}
                                  id={qrData.id}
                                  size="large"
                                  isDarkMode={false}
                                />
                              </div>
                              
                              <div className="space-y-3">
                                <button
                                  onClick={handleDownloadQR}
                                  className={`w-full py-3 rounded-lg flex items-center justify-center space-x-2 ${currentTheme.buttonBg} ${currentTheme.buttonHover} text-white font-medium`}
                                >
                                  <Download size={16} />
                                  <span>{t('qr.download', 'İndir')}</span>
                                </button>
                                
                                <button
                                  onClick={handleShareQR}
                                  className={`w-full py-3 rounded-lg flex items-center justify-center space-x-2 border ${currentTheme.cardBorder} ${currentTheme.textColor} hover:bg-gray-50 dark:hover:bg-slate-700 font-medium transition-colors`}
                                >
                                  <Share2 size={16} />
                                  <span>{t('qr.share', 'Paylaş')}</span>
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </AnimatePresence>
              )}

          {activeTab === 'identity' && (
            <div className="space-y-4">
              {/* Profile Settings Header - Made clickable to open visibility preferences modal */}
              <motion.div 
                className={`${currentTheme.cardBg} rounded-xl p-4 ${currentTheme.cardBorder} border shadow-sm cursor-pointer hover:shadow-lg transition-all duration-200`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowProfileSettings(true)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDarkModeState ? 'bg-blue-900/30' : 'bg-blue-100'
                    }`}>
                      <User size={20} className={`${isDarkModeState ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${currentTheme.textColor} flex items-center space-x-2`}>
                        <span>{t('dashboard.identityInfo', 'Kimlik Bilgileri')}</span>
                        <Settings size={16} className={`${isDarkModeState ? 'text-blue-400' : 'text-blue-600'}`} />
                      </h3>
                      <p className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-500'} hover:${isDarkModeState ? 'text-blue-400' : 'text-blue-600'} transition-colors`}>
                        {t('dashboard.profileFromSettings', 'Profil ayarlarınızdan gelen bilgiler')} • {t('dashboard.clickToCustomize', 'Özelleştirmek için tıklayın')}
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={20} className={`${isDarkModeState ? 'text-gray-400' : 'text-gray-500'} hover:${isDarkModeState ? 'text-blue-400' : 'text-blue-600'} transition-colors`} />
                </div>
              </motion.div>

              {/* Enhanced Emergency Info Card with Security Features - Updated to match ProfileScreen.jsx */}
              {profileVisibilityPrefs.showMedicalInfo && (
                <div className={`rounded-xl overflow-hidden mobile-card ${currentTheme.cardBg} ${isDarkModeState ? '' : 'shadow-md'}`}>
                <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 py-3 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="emergency-alert mr-2">
                        <AlertCircle size={20} />
                      </div>
                      <h3 className="font-bold text-lg">{t('dashboard.emergencyInfo', 'Acil Durum Bilgileri')}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  {/* Allergies Section with enhanced styling */}
                  <div className={`${isDarkModeState ? 'border-slate-700' : 'border-gray-200'}`}>
                    <h4 className={`font-semibold mb-3 flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                      <AlertCircle size={16} className="mr-2 text-red-500" />
                      {t('dashboard.allergies', 'Alerjiler')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {(formattedUser?.allergies && formattedUser.allergies.length > 0 ? 
                        formattedUser.allergies.map((allergy, idx) => {
                          const allergyName = typeof allergy === 'object' ? (allergy.name || allergy.nameEn) : allergy;
                          const allergySeverity = typeof allergy === 'object' ? allergy.severity : 'medium';
                          return (
                            <div key={idx} className={`p-3 rounded-lg ${isDarkModeState ? 'bg-red-900/20' : 'bg-red-50'} border border-red-200 dark:border-red-800 transition-all hover:shadow-md`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                  <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{sanitizeInput(allergyName)}</span>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  allergySeverity === 'high' || allergyName.toLowerCase().includes('şiddetli') 
                                    ? 'bg-red-200 text-red-800 dark:bg-red-800/50 dark:text-red-200' 
                                    : 'bg-orange-200 text-orange-800 dark:bg-orange-800/50 dark:text-orange-200'
                                }`}>
                                  {allergySeverity === 'high' || allergyName.toLowerCase().includes('şiddetli') 
                                    ? t('health.severe', 'Şiddetli') 
                                    : t('health.moderate', 'Orta')}
                                </span>
                              </div>
                            </div>
                          );
                        }) : 
                        ['Polen', 'Fıstık', 'Antibiyotik'].map((allergy, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${isDarkModeState ? 'bg-red-900/20' : 'bg-red-50'} border border-red-200 dark:border-red-800 transition-all hover:shadow-md`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{sanitizeInput(allergy)}</span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full bg-orange-200 text-orange-800 dark:bg-orange-800/50 dark:text-orange-200`}>
                                {t('health.moderate', 'Orta')}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {/* Health Conditions with enhanced styling */}
                  <div className={`my-4 border-t pt-4 ${isDarkModeState ? 'border-slate-700' : 'border-gray-200'}`}>
                    <h4 className={`font-semibold mb-3 flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                      <Activity size={16} className="mr-2 text-blue-500" />
                      {t('dashboard.healthConditions', 'Sağlık Durumu')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {(formattedUser?.medicalConditions && formattedUser.medicalConditions.length > 0 ? 
                        formattedUser.medicalConditions.map((condition, idx) => {
                          const conditionName = typeof condition === 'object' ? (condition.name || condition.nameEn) : condition;
                          const conditionSeverity = typeof condition === 'object' ? condition.severity : 'medium';
                          return (
                            <div key={idx} className={`p-3 rounded-lg ${isDarkModeState ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-200 dark:border-blue-800 transition-all hover:shadow-md`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <Activity size={16} className="text-blue-500" />
                                  <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{sanitizeInput(conditionName)}</span>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full bg-blue-200 text-blue-800 dark:bg-blue-800/50 dark:text-blue-200`}>
                                  {conditionSeverity === 'high' ? t('health.severe', 'Şiddetli') : t('health.chronic', 'Kronik')}
                                </span>
                              </div>
                            </div>
                          );
                        }) : 
                        formattedUser?.conditions && formattedUser.conditions.length > 0 ?
                        formattedUser.conditions.map((condition, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${isDarkModeState ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-200 dark:border-blue-800 transition-all hover:shadow-md`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Activity size={16} className="text-blue-500" />
                                <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{sanitizeInput(condition)}</span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full bg-blue-200 text-blue-800 dark:bg-blue-800/50 dark:text-blue-200`}>
                                {t('health.chronic', 'Kronik')}
                              </span>
                            </div>
                          </div>
                        )) :
                        ['Hipertansiyon', 'Astım'].map((condition, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${isDarkModeState ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-200 dark:border-blue-800 transition-all hover:shadow-md`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Activity size={16} className="text-blue-500" />
                                <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{sanitizeInput(condition)}</span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full bg-blue-200 text-blue-800 dark:bg-blue-800/50 dark:text-blue-200`}>
                                {t('health.chronic', 'Kronik')}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {/* Medications with enhanced layout */}
                  <div className={`my-4 border-t pt-4 ${isDarkModeState ? 'border-slate-700' : 'border-gray-200'}`}>
                    <h4 className={`font-semibold mb-3 flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                      <Heart size={16} className="mr-2 text-green-500" />
                      {t('dashboard.medications', 'İlaçlar')}
                    </h4>
                    <div className="space-y-3">
                      {(formattedUser?.medications && formattedUser.medications.length > 0 ? 
                        formattedUser.medications.map((medication, idx) => {
                          const medicationName = typeof medication === 'object' ? (medication.name || medication.nameEn) : medication;
                          const medicationSeverity = typeof medication === 'object' ? medication.severity : 'medium';
                          return (
                            <div key={idx} className={`p-4 rounded-lg ${isDarkModeState ? 'bg-green-900/20' : 'bg-green-50'} border border-green-200 dark:border-green-800 transition-all hover:shadow-md`}>
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center space-x-3">
                                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                  <div>
                                    <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'} block`}>
                                      {sanitizeInput(medicationName.split(' ')[0])} {/* Medication name */}
                                    </span>
                                    <span className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {sanitizeInput(medicationName.split(' ').slice(1).join(' '))} {/* Dosage and frequency */}
                                    </span>
                                  </div>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full bg-green-200 text-green-800 dark:bg-green-800/50 dark:text-green-200`}>
                                  {t('health.active', 'Aktif')}
                                </span>
                              </div>
                            </div>
                          );
                        }) : 
                        ['Lisinopril 10mg (günde 1)', 'Ventolin inhaler (gerektiğinde)'].map((medication, idx) => (
                          <div key={idx} className={`p-4 rounded-lg ${isDarkModeState ? 'bg-green-900/20' : 'bg-green-50'} border border-green-200 dark:border-green-800 transition-all hover:shadow-md`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <div>
                                  <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'} block`}>
                                    {sanitizeInput(medication.split(' ')[0])} {/* Medication name */}
                                  </span>
                                  <span className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {sanitizeInput(medication.split(' ').slice(1).join(' '))} {/* Dosage and frequency */}
                                  </span>
                                </div>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full bg-green-200 text-green-800 dark:bg-green-800/50 dark:text-green-200`}>
                                {t('health.active', 'Aktif')}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
              )}
              
              {/* Enhanced Emergency Contacts Card */}
              {profileVisibilityPrefs.showEmergencyContacts && (
                <div className={`rounded-xl overflow-hidden mobile-card ${currentTheme.cardBg} ${isDarkModeState ? '' : 'shadow-md'}`}>
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-3 text-white">
                    <div className="flex items-center">
                      <div className="flex items-center">
                        <Phone size={20} className="mr-2" />
                        <h3 className="font-bold text-lg">{t('dashboard.emergencyContacts', 'Acil Durum Kişileri')}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    {(formattedUser?.emergencyContacts || getDefaultEmergencyContacts()).map((contact, idx) => (
                      <div key={idx} className={`p-4 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md hover:scale-[1.02] ${idx > 0 ? 'mt-3' : ''}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              contact.relationship === 'Doktor' || contact.relation === 'Doktor' 
                                ? 'bg-green-100 dark:bg-green-900/30' 
                                : idx === 0 
                                  ? 'bg-red-100 dark:bg-red-900/30' 
                                  : 'bg-blue-100 dark:bg-blue-900/30'
                            }`}>
                              <User size={18} className={
                                contact.relationship === 'Doktor' || contact.relation === 'Doktor' 
                                  ? 'text-green-600 dark:text-green-400' 
                                  : idx === 0 
                                    ? 'text-red-600 dark:text-red-400' 
                                    : 'text-blue-600 dark:text-blue-400'
                              } />
                            </div>
                            <div>
                              <h5 className={`font-semibold ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                                {sanitizeInput(contact.name)}
                              </h5>
                              <span className={`text-sm px-2 py-1 rounded-full ${
                                contact.relationship === 'Doktor' || contact.relation === 'Doktor' 
                                  ? (isDarkModeState ? 'bg-green-900/30 text-green-300 border border-green-800/50' : 'bg-green-100 text-green-800 border border-green-200')
                                  : idx === 0 
                                    ? (isDarkModeState ? 'bg-red-900/30 text-red-300 border border-red-800/50' : 'bg-red-100 text-red-800 border border-red-200')
                                    : (isDarkModeState ? 'bg-blue-900/30 text-blue-300 border border-blue-800/50' : 'bg-blue-100 text-blue-800 border border-blue-200')
                              }`}>
                                {sanitizeInput(contact.relationship || contact.relation)}
                              </span>
                            </div>
                          </div>
                          {(contact.isPrimary || idx === 0) && (
                            <div className="flex items-center space-x-1">
                              <Star size={14} className="text-yellow-500 fill-current" />
                              <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                                {t('profile.primary', 'Birincil')}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center space-x-3">
                            <Phone size={14} className={isDarkModeState ? 'text-gray-400' : 'text-gray-600'} />
                            <span className={`font-mono text-sm ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                              {contact.phone}
                            </span>
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-xs text-green-600 dark:text-green-400">
                                {t('profile.verified', 'Doğrulandı')}
                              </span>
                            </div>
                          </div>
                          
                          {contact.location && (
                            <div className="flex items-center space-x-3">
                              <MapPin size={14} className={isDarkModeState ? 'text-gray-400' : 'text-gray-600'} />
                              <span className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>
                                {sanitizeInput(contact.location)}
                              </span>
                            </div>
                          )}
                          
                          {/* Contact actions */}
                          <div className="flex items-center space-x-2 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                            <button 
                              onClick={() => window.open(`tel:${contact.phone.replace(/\s+/g, '')}`, '_self')}
                              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                isDarkModeState 
                                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                                  : 'bg-green-500 hover:bg-green-600 text-white'
                              }`}
                            >
                              {t('profile.call', 'Ara')}
                            </button>
                            <button className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                              isDarkModeState 
                                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}>
                              {t('profile.message', 'Mesaj')}
                            </button>
                            {(contact.relationship === 'Doktor' || contact.relation === 'Doktor') && (
                              <button className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                isDarkModeState 
                                  ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                  : 'bg-purple-500 hover:bg-purple-600 text-white'
                              }`}>
                                {t('profile.appointment', 'Randevu')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <button 
                      onClick={handleAddEmergencyContact}
                      className={`mt-4 w-full py-3 border-2 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-[1.02] ${
                        isDarkModeState 
                          ? 'border-blue-500 text-blue-300 hover:bg-blue-500/20 hover:border-blue-400' 
                          : 'border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600'
                      }`}
                    >
                      <Plus size={18} className="mr-2" />
                      {t('dashboard.addEmergencyContact', 'Acil Durum Kişisi Ekle')}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Enhanced Personal Info Card */}
              {profileVisibilityPrefs.showPersonalInfo && (
                <div className={`rounded-xl overflow-hidden mobile-card ${currentTheme.cardBg} ${isDarkModeState ? '' : 'shadow-md'}`}>
                  <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3 text-white">
                    <div className="flex items-center">
                      <div className="flex items-center">
                        <User size={20} className="mr-2" />
                        <h3 className="font-bold text-lg">{t('dashboard.personalInfo', 'Kişisel Bilgiler')}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    {/* Languages section */}
                    <div className={`mb-4 pb-4 border-b ${isDarkModeState ? 'border-slate-700' : 'border-gray-200'}`}>
                      <h4 className={`mb-3 font-semibold flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                        <Globe size={16} className="mr-2" />
                        {t('profile.languages', 'Konuşulan Diller')}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {(formattedUser?.languages || ['Türkçe', 'English', 'Deutsch']).map((language, idx) => (
                          <span key={idx} className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                            isDarkModeState ? 'bg-teal-900/30 text-teal-300 border border-teal-800/50' : 'bg-teal-100 text-teal-800 border border-teal-200'
                          } transition-transform hover:scale-105`}>
                            {sanitizeInput(language)}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Hobbies section */}
                    {formattedUser?.hobbies && formattedUser.hobbies.length > 0 && (
                      <div className={`mb-4 pb-4 border-b ${isDarkModeState ? 'border-slate-700' : 'border-gray-200'}`}>
                        <h4 className={`mb-3 font-semibold flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                          <Heart size={16} className="mr-2" />
                          {t('profile.hobbies', 'Hobiler')}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {formattedUser.hobbies.map((hobby, idx) => (
                            <span key={idx} className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                              isDarkModeState ? 'bg-green-900/30 text-green-300 border border-green-800/50' : 'bg-green-100 text-green-800 border border-green-200'
                            } transition-transform hover:scale-105`}>
                              {sanitizeInput(hobby)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Interests section */}
                    {formattedUser?.interests && formattedUser.interests.length > 0 && (
                      <div className={`mb-4 pb-4 border-b ${isDarkModeState ? 'border-slate-700' : 'border-gray-200'}`}>
                        <h4 className={`mb-3 font-semibold flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                          <Settings size={16} className="mr-2" />
                          {t('profile.interests', 'İlgi Alanları')}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {formattedUser.interests.map((interest, idx) => (
                            <span key={idx} className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                              isDarkModeState ? 'bg-purple-900/30 text-purple-300 border border-purple-800/50' : 'bg-purple-100 text-purple-800 border border-purple-200'
                            } transition-transform hover:scale-105`}>
                              {sanitizeInput(interest)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Enhanced contact information */}
                    <div className={`mb-4 pb-4 border-b ${isDarkModeState ? 'border-slate-700' : 'border-gray-200'}`}>
                      <h4 className={`mb-3 font-semibold flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                        <Mail size={16} className="mr-2" />
                        {t('profile.contacts', 'İletişim Bilgileri')}
                      </h4>
                      <div className="space-y-3">
                        {(formattedUser?.phones || [formattedUser?.safePhone]).map((phone, idx) => (
                          <div key={`phone-${idx}`} className={`flex items-center justify-between p-3 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                            <div className="flex items-center space-x-3">
                              <Phone size={16} className={isDarkModeState ? 'text-gray-400' : 'text-gray-600'} />
                              <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{phone}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {isValidPhoneNumber && isValidPhoneNumber(phone) && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  isDarkModeState ? 'bg-green-900/30 text-green-300 border border-green-800/50' : 'bg-green-100 text-green-800 border border-green-200'
                                }`}>
                                  Doğrulandı
                                </span>
                              )}
                              <button 
                                onClick={() => navigator.clipboard?.writeText(phone)}
                                className={`text-xs px-2 py-1 rounded ${
                                  isDarkModeState ? 'bg-blue-900/30 text-blue-300 border border-blue-800/50' : 'bg-blue-100 text-blue-800 border border-blue-200'
                                } hover:scale-105 transition-transform`}
                                aria-label={`${phone} numarasını kopyala`}
                              >
                                Kopyala
                              </button>
                            </div>
                          </div>
                        ))}
                        
                        {(formattedUser?.emails || [formattedUser?.safeEmail]).map((email, idx) => (
                          <div key={`email-${idx}`} className={`flex items-center justify-between p-3 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                            <div className="flex items-center space-x-3">
                              <Mail size={16} className={isDarkModeState ? 'text-gray-400' : 'text-gray-600'} />
                              <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'} text-sm`}>{email}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {isValidEmail && isValidEmail(email) && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  isDarkModeState ? 'bg-green-900/30 text-green-300 border border-green-800/50' : 'bg-green-100 text-green-800 border border-green-200'
                                }`}>
                                  Doğrulandı
                                </span>
                              )}
                              <button 
                                onClick={() => navigator.clipboard?.writeText(email)}
                                className={`text-xs px-2 py-1 rounded ${
                                  isDarkModeState ? 'bg-blue-900/30 text-blue-300 border border-blue-800/50' : 'bg-blue-100 text-blue-800 border border-blue-200'
                                } hover:scale-105 transition-transform`}
                                aria-label={`${email} adresini kopyala`}
                              >
                                Kopyala
                              </button>
                            </div>
                          </div>
                        ))}
                        
                        <div className={`flex items-center space-x-3 p-3 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                          <MapPin size={16} className={isDarkModeState ? 'text-gray-400' : 'text-gray-600'} />
                          <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{formattedUser?.address || 'İstanbul, Türkiye'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Enhanced Bio Section */}
                    <div>
                      <h4 className={`mb-3 font-semibold flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                        <User size={16} className="mr-2" />
                        {t('profile.bio', 'Hakkımda')}
                      </h4>
                      <div className={`p-4 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                        <p className={`text-sm leading-relaxed ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>
                          {formattedUser?.safeBio}
                        </p>
                      </div>
                    </div>

                    {/* Additional Notes Section */}
                    {formattedUser?.notes && (
                      <div className="mt-4">
                        <h4 className={`mb-3 font-semibold flex items-center ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                          <FileText size={16} className="mr-2" />
                          {t('profile.additionalNotes', 'Ek Notlar')}
                        </h4>
                        <div className={`p-4 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                          <p className={`text-sm leading-relaxed ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>
                            {sanitizeInput(formattedUser.notes)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Enhanced Physical Information Card - Added to match ProfileScreen.jsx */}
              {profileVisibilityPrefs.showPhysicalInfo && (
                <div className={`rounded-xl overflow-hidden mobile-card ${currentTheme.cardBg} ${isDarkModeState ? '' : 'shadow-md'}`}>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Activity size={20} className="mr-2" />
                      <h3 className="font-bold text-lg">{t('profile.physical', 'Fiziksel Bilgiler')}</h3>
                    </div>
                    <button 
                      className="bg-white/20 hover:bg-white/30 p-2 rounded-full hover:scale-105 transition-all duration-200"
                      onClick={() => {
                        console.log('🔄 DashboardScreen: Navigating to profile edit from physical info...');
                        navigateToScreen('profileEdit', { section: 'physical' });
                      }}
                    >
                      <Edit size={16} className="text-white" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {/* Primary physical stats layout */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className={`p-4 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-transform hover:scale-105`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Activity size={16} className="text-purple-500" />
                        <span className={`text-sm font-medium ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>{t('profile.height', 'Boy')}</span>
                      </div>
                      <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                        {formattedUser?.height ? 
                          (formattedUser.height.toString().includes('cm') ? formattedUser.height : `${formattedUser.height} cm`) : 
                          '185 cm'
                        }
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-transform hover:scale-105`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Activity size={16} className="text-purple-500" />
                        <span className={`text-sm font-medium ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>{t('profile.weight', 'Kilo')}</span>
                      </div>
                      <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                        {formattedUser?.weight ? 
                          (formattedUser.weight.toString().includes('kg') ? formattedUser.weight : `${formattedUser.weight} kg`) : 
                          '80 kg'
                        }
                      </div>
                    </div>
                  </div>
                  
                  {/* Blood type prominently displayed - Note: Different from medical section for emergency purposes */}
                  <div className={`p-4 rounded-lg ${isDarkModeState ? 'bg-red-900/20' : 'bg-red-50'} border border-red-200 dark:border-red-800 mb-4`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Heart size={20} className="text-red-500" />
                        <span className={`text-lg font-semibold ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{t('profile.bloodType', 'Kan Grubu')}</span>
                      </div>
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/50 px-4 py-2 rounded-lg">
                        {formattedUser?.validatedBloodType || formattedUser?.bloodType || 'A Rh+'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Physical characteristics grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    <div className={`p-3 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-green-400"></div>
                          <span className={`text-sm font-medium ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>{t('profile.eyeColor', 'Göz Rengi')}</span>
                        </div>
                        <span className={`text-sm font-bold ${isDarkModeState ? 'text-white' : 'text-gray-800'} capitalize`}>
                          {sanitizeInput(formattedUser?.eyeColor) || 'Kahverengi'}
                        </span>
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-400 to-orange-400"></div>
                          <span className={`text-sm font-medium ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>{t('profile.hairColor', 'Saç Rengi')}</span>
                        </div>
                        <span className={`text-sm font-bold ${isDarkModeState ? 'text-white' : 'text-gray-800'} capitalize`}>
                          {sanitizeInput(formattedUser?.hairColor) || 'Siyah'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Additional personal characteristics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                    {formattedUser?.gender && (
                      <div className={`p-3 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <User size={16} className="text-purple-500" />
                            <span className={`text-sm font-medium ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>{t('profile.gender', 'Cinsiyet')}</span>
                          </div>
                          <span className={`text-sm font-bold ${isDarkModeState ? 'text-white' : 'text-gray-800'} capitalize`}>
                            {sanitizeInput(formattedUser.gender)}
                          </span>
                        </div>
                      </div>
                    )}
                    {formattedUser?.dateOfBirth && (
                      <div className={`p-3 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Calendar size={16} className="text-purple-500" />
                            <span className={`text-sm font-medium ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>{t('profile.dateOfBirth', 'Doğum Tarihi')}</span>
                          </div>
                          <span className={`text-sm font-bold ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                            {formatDate ? formatDate(formattedUser.dateOfBirth, 'short') : formattedUser.dateOfBirth}
                          </span>
                        </div>
                      </div>
                    )}
                    {formattedUser?.nationality && (
                      <div className={`p-3 rounded-lg ${isDarkModeState ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Globe size={16} className="text-purple-500" />
                            <span className={`text-sm font-medium ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>{t('profile.nationality', 'Uyruk')}</span>
                          </div>
                          <span className={`text-sm font-bold ${isDarkModeState ? 'text-white' : 'text-gray-800'} capitalize`}>
                            {sanitizeInput(formattedUser.nationality)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* BMI Calculation if height and weight are available */}
                  {formattedUser?.height && formattedUser?.weight && (
                    <div className={`p-3 rounded-lg ${isDarkModeState ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-200 dark:border-blue-800`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <BarChart size={16} className="text-blue-500" />
                          <span className={`text-sm font-medium ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'}`}>BMI</span>
                        </div>
                        <span className={`text-sm font-bold ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>
                          {(() => {
                            const heightStr = formattedUser.height.toString().replace(/[^\d]/g, '');
                            const weightStr = formattedUser.weight.toString().replace(/[^\d]/g, '');
                            const height = parseFloat(heightStr) / 100; // Convert cm to meters
                            const weight = parseFloat(weightStr);
                            const bmi = weight / (height * height);
                            return isNaN(bmi) ? 'N/A' : bmi.toFixed(1);
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Chronic Diseases Section - If exists */}
              {formattedUser?.chronicDiseases && formattedUser.chronicDiseases.length > 0 && (
                <div className={`rounded-xl overflow-hidden mobile-card ${currentTheme.cardBg} ${isDarkModeState ? '' : 'shadow-md'}`}>
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3 text-white">
                    <div className="flex items-center">
                      <Shield size={20} className="mr-2" />
                      <h3 className="font-bold text-lg">{t('dashboard.chronicDiseases', 'Kronik Hastalıklar')}</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 gap-2">
                      {formattedUser.chronicDiseases.map((disease, idx) => (
                        <div key={idx} className={`p-3 rounded-lg ${isDarkModeState ? 'bg-purple-900/20' : 'bg-purple-50'} border border-purple-200 dark:border-purple-800 transition-all hover:shadow-md`}>
                          <div className="flex items-center space-x-3">
                            <Shield size={16} className="text-purple-500" />
                            <span className={`font-medium ${isDarkModeState ? 'text-white' : 'text-gray-800'}`}>{sanitizeInput(disease)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'statistics' && (
            <div className="space-y-4">
              {/* Yükleniyor veya hata durumu */}
              {isLoading && (
                <div className="text-center py-8 text-lg font-medium">Yükleniyor...</div>
              )}
              {error && (
                <div className="text-center py-8 text-red-500 font-medium">Hata: {error}</div>
              )}
              {/* Unified stats yüklendiyse göster */}
              {unifiedStats && (
                <div className={`mb-4 ${isDarkModeState ? 'bg-slate-800' : 'bg-white'} rounded-xl overflow-hidden shadow-sm`}>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-white">
                    <div className="flex items-center">
                      <BarChart size={20} className="mr-2" />
                      <h3 className="font-bold text-lg">{t('dashboard.userStats', 'Kullanıcı İstatistikleri')}</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    {/* QR İstatistikleri */}
                    <div className="mb-6">
                      <h4 className="font-medium text-lg mb-3">{t('dashboard.qrStatistics', 'QR İstatistikleri')}</h4>
                      <div className="flex justify-between mb-4">
                        <div className="text-center">
                          <h4 className="font-bold text-lg">{unifiedStats.qrStats.totalQRs}</h4>
                          <p className={isDarkModeState ? 'text-gray-400' : 'text-gray-500'}>{t('dashboard.totalQRs', 'Toplam QR')}</p>
                        </div>
                        <div className="text-center">
                          <h4 className="font-bold text-lg">{unifiedStats.qrStats.totalViews}</h4>
                          <p className={isDarkModeState ? 'text-gray-400' : 'text-gray-500'}>{t('dashboard.totalViews', 'Toplam Görüntüleme')}</p>
                        </div>
                        <div className="text-center">
                          <h4 className="font-bold text-lg">{unifiedStats.qrStats.weeklyGrowth}%</h4>
                          <p className={isDarkModeState ? 'text-gray-400' : 'text-gray-500'}>{t('dashboard.weeklyGrowth', 'Haftalık Artış')}</p>
                        </div>
                      </div>
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={unifiedStats.qrStats.categoryData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              innerRadius={30}
                              fill="#8884d8"
                              paddingAngle={5}
                              label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {unifiedStats.qrStats.categoryData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} stroke={isDarkModeState ? '#1e293b' : '#ffffff'} strokeWidth={2} />
                              ))}
                            </Pie>
                            <Tooltip 
                              formatter={(value, name) => [`${value} görüntülenme`, name]}
                              contentStyle={{
                                backgroundColor: isDarkModeState ? '#334155' : '#ffffff',
                                borderColor: isDarkModeState ? '#475569' : '#e2e8f0',
                              }}
                            />
                            <Legend 
                              layout="horizontal" 
                              verticalAlign="bottom" 
                              align="center" 
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h5 className="text-sm font-medium mb-2">{t('dashboard.topPerformingQRs', 'En Çok Görüntülenen QR Kodları')}</h5>
                        <div className="space-y-2">
                          {unifiedStats.qrStats.topQRs.map((qr, idx) => (
                            <div key={idx} className="flex justify-between items-center">
                              <span className={isDarkModeState ? 'text-gray-300' : 'text-gray-700'}>{qr.name}</span>
                              <span className="text-sm font-medium">{qr.views} {t('common.views', 'görüntülenme')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Dijital Varlık ve Sosyal İstatistikler için örnek alanlar, unifiedStats içeriğine göre güncellenebilir */}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.main>
      
      {/* Bottom Navigation */}
      <BottomNavigation />
      
      {/* Security Verification */}
      {showSecurity && <SecurityVerification />}

      {/* Profile Settings Modal */}
      <AnimatePresence>
        {showProfileSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowProfileSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`${currentTheme.cardBg} rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold ${currentTheme.textColor}`}>
                  {t('profile.visibilitySettings', 'Görünürlük Ayarları')}
                </h3>
                <button
                  onClick={() => setShowProfileSettings(false)}
                  className={`p-2 rounded-lg ${isDarkModeState ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <X size={20} className={currentTheme.iconColor} />
                </button>
              </div>
              
              <div className="space-y-4">
                <p className={`text-sm ${isDarkModeState ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  {t('profile.visibilityDesc', 'Kimlik bölümünde hangi bilgilerin görüneceğini seçin. Bu ayarlar sadece bu ekranda gösterilen bilgileri etkiler.')}
                </p>
                
                {/* Visibility Options - Enhanced with better descriptions */}
                {[
                  { 
                    key: 'showMedicalInfo', 
                    label: t('profile.showMedicalInfo', 'Acil Durum & Sağlık Bilgileri'), 
                    icon: Heart,
                    description: t('profile.medicalInfoDesc', 'Alerjiler, sağlık durumu ve ilaçlar')
                  },
                  { 
                    key: 'showEmergencyContacts', 
                    label: t('profile.showEmergencyContacts', 'Acil Durum Kişileri'), 
                    icon: Phone,
                    description: t('profile.emergencyContactsDesc', 'Acil durumlarda ulaşılacak kişiler')
                  },
                  { 
                    key: 'showPersonalInfo', 
                    label: t('profile.showPersonalInfo', 'Kişisel Bilgiler'), 
                    icon: User,
                    description: t('profile.personalInfoDesc', 'Dil, hobi, ilgi alanları ve iletişim')
                  },
                  { 
                    key: 'showPhysicalInfo', 
                    label: t('profile.showPhysicalInfo', 'Fiziksel Bilgiler'), 
                    icon: Activity,
                    description: t('profile.physicalInfoDesc', 'Boy, kilo, kan grubu ve fiziksel özellikler')
                  },
                  { 
                    key: 'allowPublicAccess', 
                    label: t('profile.allowPublicAccess', 'Herkese Açık Erişim'), 
                    icon: Globe,
                    description: t('profile.publicAccessDesc', 'QR kod ile herkesi bilgilerinize erişim')
                  }
                ].map((option) => (
                  <div key={option.key} className={`p-4 rounded-lg border transition-all duration-200 ${
                    profileVisibilityPrefs[option.key] 
                      ? (isDarkModeState ? 'bg-blue-900/20 border-blue-500/50' : 'bg-blue-50 border-blue-200')
                      : (isDarkModeState ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200')
                  } hover:scale-[1.02]`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          profileVisibilityPrefs[option.key] 
                            ? (isDarkModeState ? 'bg-blue-500/30' : 'bg-blue-100')
                            : (isDarkModeState ? 'bg-gray-600/30' : 'bg-gray-100')
                        }`}>
                          <option.icon size={16} className={
                            profileVisibilityPrefs[option.key] 
                              ? (isDarkModeState ? 'text-blue-400' : 'text-blue-600')
                              : currentTheme.iconColor
                          } />
                        </div>
                        <div className="flex-1">
                          <span className={`font-medium ${currentTheme.textColor} block`}>{option.label}</span>
                          <span className={`text-xs ${isDarkModeState ? 'text-gray-400' : 'text-gray-500'}`}>
                            {option.description}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleProfileVisibilityChange(option.key, !profileVisibilityPrefs[option.key])}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          profileVisibilityPrefs[option.key] 
                            ? 'bg-blue-500' 
                            : isDarkModeState ? 'bg-gray-600' : 'bg-gray-300'
                        }`}
                      >
                        <motion.div
                          className="w-5 h-5 bg-white rounded-full shadow-md"
                          animate={{ x: profileVisibilityPrefs[option.key] ? 24 : 2 }}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      </button>
                    </div>
                  </div>
                ))}
                
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => navigateToScreen('profileEdit')}
                    className={`w-full py-3 rounded-lg font-medium ${currentTheme.buttonBg} ${currentTheme.buttonHover} text-white transition-colors`}
                  >
                    {t('profile.editProfile', 'Profili Düzenle')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DashboardScreen;
